import { useEffect, useRef } from 'react';

import {
  estimateCadenceHz,
  type FrameEpisodeResult,
  FROZEN_FRAME_MS,
  getFrameSamplerState,
  JANK_THRESHOLD_MS,
  LONG_FRAME_MS,
  subscribeFrameEpisodes,
} from '@heidi/runtime/performance';

/**
 * Ring of recent frame gaps. Sized for the one-second window on a fast panel:
 * 120 entries is only 0.83 s at 144 Hz, which silently shortened the window on
 * exactly the displays this strip exists to make legible.
 */
const RING_SIZE = 256;
const WINDOW_MS = 1_000;
/** DOM writes are throttled to this so the strip never becomes the jank it shows. */
const PAINT_INTERVAL_MS = 250;
/** Episode rows shown, newest first. Older surfaces drop off the bottom. */
const MAX_EPISODE_ROWS = 5;

function formatAge(nowWall: number, completedAt: number): string {
  const seconds = Math.max(0, Math.round((nowWall - completedAt) / 1000));
  return seconds < 60 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
}

function formatEpisode(episode: FrameEpisodeResult, nowWall: number): string {
  const trigger = { scroll: 'scroll', transition: 'anim', pointer: 'ptr', key: 'key' }[
    episode.trigger
  ];
  return `${trigger} · ${episode.frames} fr · ${episode.jank_pct}% jank · worst ${episode.worst_gap_ms}ms · ${episode.cadence_hz}Hz · ${formatAge(nowWall, episode.completedAt)}`;
}

/**
 * Live frame metrics strip for the perf debug panel (ARD-1977, D6). Three
 * parts, each written to the DOM through refs:
 *
 * 1. Live frames from the strip's own rAF loop — runs whenever the panel is
 *    open, regardless of the sampling gate, so a developer can see frame
 *    health before deciding whether the gate is why no episode appears.
 *    `fps` is printed as delivered/panel (`112/120`) and beside a `jank %`
 *    computed the way the shipped metric computes it, so the strip cannot be
 *    read as "this display is slower than that one".
 * 2. Gate state — enabled, admissions, next admission countdown. Reads
 *    only; never admits.
 * 3. Episodes — one row per surface that has reported, newest first, capped
 *    at MAX_EPISODE_ROWS. Rows only exist once a surface has reported; until
 *    then a single hint line says why nothing is there (sampler off, or on
 *    but nothing scrolled yet).
 *
 * Zero React re-renders on a frame tick: the loop keeps a fixed ring of
 * numbers and paints at most four times a second. Dev-only: the panel that
 * hosts it is mounted only under `NEXT_PUBLIC_PERF_DEBUG`.
 */
export function FrameLiveMetrics({ active }: { active: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef<HTMLSpanElement>(null);
  const gateRef = useRef<HTMLSpanElement>(null);
  const episodesRef = useRef<HTMLDivElement>(null);
  /** Insertion-ordered; re-inserting a surface moves it to the end (newest). */
  const lastEpisodes = useRef(new Map<string, FrameEpisodeResult>());

  useEffect(
    () =>
      subscribeFrameEpisodes((episode) => {
        lastEpisodes.current.delete(episode.surface);
        lastEpisodes.current.set(episode.surface, episode);
      }),
    []
  );

  useEffect(() => {
    if (!active || typeof requestAnimationFrame !== 'function') return;

    const gaps = new Float64Array(RING_SIZE);
    let head = 0;
    let filled = 0;
    let worst = 0;
    // The first rAF timestamp can precede the performance.now() taken here
    // (Chrome hands the current frame's start time to a callback registered
    // mid-frame), so the first tick only seeds `last` and records no gap.
    let last = Number.NaN;
    let lastPainted = -Infinity;
    let rafId = 0;
    let stopped = false;
    let paintCount = 0;

    const paintEpisodes = (nowWall: number, enabled: boolean) => {
      const container = episodesRef.current;
      if (!container) return;
      const episodes = [...lastEpisodes.current.values()]
        .reverse()
        .slice(0, MAX_EPISODE_ROWS);

      if (episodes.length === 0) {
        const hint = enabled
          ? 'none yet · scroll a list or open a panel'
          : 'none · sampler off · set NEXT_PUBLIC_PERF_DEBUG=true';
        if (container.dataset.state !== 'empty') {
          container.replaceChildren();
          container.dataset.state = 'empty';
        }
        container.textContent = hint;
        return;
      }

      // Reconcile rows by surface; the DOM stays stable while values update.
      if (container.dataset.state !== 'rows') {
        container.replaceChildren();
        container.dataset.state = 'rows';
      }
      const rows = container.children;
      for (let i = 0; i < episodes.length; i++) {
        const episode = episodes[i]!;
        let row = rows[i] as HTMLDivElement | undefined;
        if (!row) {
          row = document.createElement('div');
          row.className = 'flex gap-2';
          const label = document.createElement('span');
          label.className = 'w-32 shrink-0 truncate text-bark-400';
          const value = document.createElement('span');
          value.className = 'min-w-0 flex-1 truncate text-bark-200';
          row.append(label, value);
          container.appendChild(row);
        }
        const [label, value] = row.children as unknown as [
          HTMLSpanElement,
          HTMLSpanElement,
        ];
        if (label.textContent !== episode.surface) {
          label.textContent = episode.surface;
          label.title = episode.surface;
          row.dataset.testid = `perf-frame-surface-${episode.surface}`;
        }
        value.textContent = formatEpisode(episode, nowWall);
      }
      while (rows.length > episodes.length) container.lastElementChild?.remove();
    };

    const paint = (now: number) => {
      // Walk back from the newest gap until one second is covered.
      let count = 0;
      let sum = 0;
      let long = 0;
      let frozen = 0;
      let overshoot = 0;
      const window: number[] = [];
      for (let i = 0; i < filled && sum < WINDOW_MS; i++) {
        const gap = gaps[(head - 1 - i + RING_SIZE) % RING_SIZE] ?? 0;
        sum += gap;
        count += 1;
        if (gap > LONG_FRAME_MS) long += 1;
        if (gap > FROZEN_FRAME_MS) frozen += 1;
        // The shipped metric's rule, so the strip and `jank_pct` agree.
        if (gap > JANK_THRESHOLD_MS) overshoot += gap - JANK_THRESHOLD_MS;
        window.push(gap);
      }
      // The delivered cadence, from the whole ring rather than the one-second
      // window, by the same estimator the shipped `cadence_hz` uses. It is
      // printed beside fps because `fps` alone is what made a 75 Hz external
      // display look like a regression against a 60 Hz desktop build.
      const panelHz = estimateCadenceHz(gaps, filled);
      window.sort((a, b) => a - b);
      const p95 = window.length
        ? (window[Math.floor((window.length - 1) * 0.95)] ?? 0)
        : 0;
      const fps = sum > 0 ? Math.round((count / sum) * 1000) : 0;
      const jankPct = sum > 0 ? Math.round((overshoot / sum) * 1000) / 10 : 0;

      if (liveRef.current) {
        liveRef.current.textContent = `${fps}/${panelHz} fps · jank ${jankPct}% · p95 ${Math.round(p95)}ms · long ${long} · frozen ${frozen} · worst ${Math.round(worst)}ms`;
      }

      const state = getFrameSamplerState(now);
      if (gateRef.current) {
        const next =
          state.nextAdmissionInMs > 0
            ? `next in ${Math.ceil(state.nextAdmissionInMs / 1000)}s`
            : 'ready';
        const ceiling = state.exhausted
          ? `ceiling reached · resets in ${Math.ceil(state.windowResetsInMs / 1000)}s`
          : next;
        gateRef.current.textContent = state.enabled
          ? `sampler on · ${state.admissions} admitted · ${ceiling}`
          : 'sampler off';
      }

      paintEpisodes(Date.now(), state.enabled);

      paintCount += 1;
      rootRef.current?.setAttribute('data-paint-count', String(paintCount));
    };

    const tick = (timestamp: number) => {
      if (stopped) return;
      const gap = Number.isNaN(last) ? Number.NaN : Math.max(0, timestamp - last);
      last = timestamp;
      if (Number.isNaN(gap)) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      gaps[head] = gap;
      head = (head + 1) % RING_SIZE;
      if (filled < RING_SIZE) filled += 1;
      if (gap > worst) worst = gap;
      if (timestamp - lastPainted >= PAINT_INTERVAL_MS) {
        lastPainted = timestamp;
        paint(timestamp);
      }
      rafId = requestAnimationFrame(tick);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        cancelAnimationFrame(rafId);
        rafId = 0;
      } else if (!stopped && rafId === 0) {
        last = Number.NaN;
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [active]);

  return (
    <div
      ref={rootRef}
      data-testid="perf-frame-live-metrics"
      className="border-b border-bark-600 px-3 py-1.5 text-[11px] leading-5"
    >
      <div className="flex gap-2">
        <span className="w-32 shrink-0 text-bark-400">frames</span>
        <span ref={liveRef} className="min-w-0 flex-1 truncate text-bark-200">
          —
        </span>
      </div>
      <div className="flex gap-2">
        <span className="w-32 shrink-0 text-bark-400">gate</span>
        <span ref={gateRef} className="min-w-0 flex-1 truncate text-bark-200">
          —
        </span>
      </div>
      <div className="mt-1 text-bark-400">episodes</div>
      <div
        ref={episodesRef}
        data-testid="perf-frame-episodes"
        data-state="empty"
        className="text-bark-200"
      >
        —
      </div>
    </div>
  );
}
